/**
 * ========================================
 * TRIAL SERVICE
 * ========================================
 * Handles trial status logic and calculations
 */

import type { User } from '@/types/auth';
import type { UserSubscription } from '@/types/payment';

export interface TrialStatus {
  isInTrial: boolean;
  isInGracePeriod: boolean;
  isExpired: boolean;
  daysRemaining: number;
  graceDaysRemaining: number;
  canAccess: boolean;
  statusMessage: string;
}

class TrialService {
  // Trial configuration (in days) -- mirrors settings.TRIAL_DURATION_DAYS on the backend
  private readonly TRIAL_DURATION = 14; // 14 days free trial
  private readonly GRACE_PERIOD = 3; // 3 days grace period

  /**
   * Calculate trial status from the user's current subscription (source of
   * truth: UserSubscription.status/trialEndsAt from the backend, not local
   * user fields -- a subscription is fetched once via
   * subscriptionService.getCurrent() and passed in here).
   */
  getTrialStatus(subscription: UserSubscription | null): TrialStatus {
    if (!subscription || subscription.status !== 'trial' || !subscription.trialEndsAt) {
      const isExpired = subscription?.status === 'expired';
      return {
        isInTrial: false,
        isInGracePeriod: false,
        isExpired,
        daysRemaining: 0,
        graceDaysRemaining: 0,
        canAccess: false,
        statusMessage: isExpired ? 'Trial expired. Please upgrade to continue.' : '',
      };
    }

    const now = new Date();
    const trialEndDate = new Date(subscription.trialEndsAt);
    const graceEndDate = new Date(trialEndDate.getTime() + this.GRACE_PERIOD * 24 * 60 * 60 * 1000);

    // Check if in trial
    const isInTrial = now < trialEndDate;
    const daysRemaining = Math.max(0, Math.ceil((trialEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));

    // Check if in grace period
    const isInGracePeriod = now < graceEndDate && !isInTrial;
    const graceDaysRemaining = Math.max(0, Math.ceil((graceEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));

    // Check if completely expired
    const isExpired = now >= graceEndDate;

    // User can access if in trial or in grace period
    const canAccess = isInTrial || isInGracePeriod;

    let statusMessage = '';
    if (isInTrial) {
      statusMessage = `Free trial active - ${daysRemaining} days remaining`;
    } else if (isInGracePeriod) {
      statusMessage = `Grace period active - ${graceDaysRemaining} days remaining. Please upgrade to continue.`;
    } else if (isExpired) {
      statusMessage = 'Trial and grace period expired. Please upgrade to continue.';
    }

    return {
      isInTrial,
      isInGracePeriod,
      isExpired,
      daysRemaining,
      graceDaysRemaining,
      canAccess,
      statusMessage,
    };
  }

  /**
   * Initialize trial for new user
   */
  initializeUserTrial(): Partial<User> {
    const now = new Date();
    const trialEnd = new Date(now.getTime() + this.TRIAL_DURATION * 24 * 60 * 60 * 1000);
    const graceEnd = new Date(trialEnd.getTime() + this.GRACE_PERIOD * 24 * 60 * 60 * 1000);

    return {
      subscription_plan: 'free',
      trial_started_at: now.toISOString(),
      trial_end_date: trialEnd.toISOString(),
      grace_period_end_date: graceEnd.toISOString(),
      is_trial_active: true,
      is_grace_period_active: false,
      subscription_status: 'trial',
    };
  }

  /**
   * Check if user has limited features (free trial users)
   */
  hasLimitedFeatures(user: User | null): boolean {
    if (!user) return true;
    return user.subscription_plan === 'free' || user.subscription_status === 'trial' || user.subscription_status === 'grace_period';
  }

  /**
   * Get available features for subscription plan
   */
  getAvailableFeatures(user: User | null): {
    canCreateCourse: boolean;
    canUseVoice: boolean;
    canAccessAI: boolean;
    canExportDocuments: boolean;
    maxCourses: number;
    maxStorageGB: number;
  } {
    if (!user || user.subscription_plan === 'free') {
      return {
        canCreateCourse: true,
        canUseVoice: false,
        canAccessAI: true,
        canExportDocuments: false,
        maxCourses: 1,
        maxStorageGB: 1,
      };
    }

    if (user.subscription_plan === 'premium') {
      return {
        canCreateCourse: true,
        canUseVoice: true,
        canAccessAI: true,
        canExportDocuments: true,
        maxCourses: 10,
        maxStorageGB: 50,
      };
    }

    // Enterprise
    return {
      canCreateCourse: true,
      canUseVoice: true,
      canAccessAI: true,
      canExportDocuments: true,
      maxCourses: 999,
      maxStorageGB: 500,
    };
  }

  /**
   * Format date for display
   */
  formatDate(date: string | undefined): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }
}

export const trialService = new TrialService();
